import * as Sentry from "@sentry/react";
import { useEffect, useMemo, useRef, type RefObject } from "react";
import type { Editor as TiptapEditor } from "@tiptap/react";
import { useSearchableRegistry, type Searchable } from "./SearchableContext";
import { searchTiptapDoc, collectTiptapMatchPositions, extractTextSegments, searchTextSegments, type SearchResult, type TextSegment } from "./utils";

/**
 * Editor wrapper that contains a tiptap instance
 * This matches the structure used in TiptapDocumentSB
 */
export interface EditorWrapper {
  tiptap: TiptapEditor;
}

export interface TiptapSearchableOptions {
  /** Unique identifier (defaults to "document") */
  id?: string;
  /** Display name (defaults to "Document") */
  name?: string;
  /** Ref to the scroll container */
  scrollHostRef?: RefObject<HTMLElement | null>;
  /** The #editor host element in the main document */
  editorHostNode?: HTMLElement | null;
}

/**
 * Adapts a TipTap editor to the Searchable interface
 * @param editor - The editor object containing tiptap instance
 * @param options - Configuration options
 */
export function useTiptapSearchable(
  editor: EditorWrapper | null | undefined,
  options: TiptapSearchableOptions = {}
): Searchable | null {
  const {
    id = "document",
    name = "Document",
    scrollHostRef,
    editorHostNode,
  } = options;

  const { register, unregister } = useSearchableRegistry();

  // PERF: Text segment cache for the document editor
  const textCacheRef = useRef<{ doc: any; segments: TextSegment[] }>({ doc: null, segments: [] });

  const searchable = useMemo<Searchable | null>(() => {
    if (!editor?.tiptap) return null;

    const tiptap = editor.tiptap;

    const getCachedSegments = () => {
      const doc = tiptap.state.doc;
      if (textCacheRef.current.doc === doc) {
        return textCacheRef.current.segments;
      }
      const segments = extractTextSegments(doc);
      textCacheRef.current = { doc, segments };
      return segments;
    };

    return {
      id,
      name,

      search: (term: string): SearchResult[] => searchTiptapDoc(tiptap.state.doc, term, id, name),

      searchWithRegex: (regex: RegExp): SearchResult[] => {
        const segments = getCachedSegments();
        return searchTextSegments(segments, regex, id, name);
      },

      replace: (result: SearchResult, replacement: string) => {
        tiptap.chain()
          .insertContentAt({ from: result.from, to: result.to }, replacement)
          .run();
      },

      replaceAll: (term: string, replacement: string) => {
        const positions = collectTiptapMatchPositions(tiptap.state.doc, term);
        if (!positions.length) return;

        // Positions are already sorted descending, so replacements maintain valid positions
        const tr = tiptap.state.tr;
        for (const { from, to } of positions) {
          tr.insertText(replacement, from, to);
        }
        tiptap.view.dispatch(tr);
      },

      scrollToResult: (result: SearchResult) => {
        if (!editorHostNode || !scrollHostRef?.current) return;

        try {
          // Get the position in the editor
          const { from, to } = result;
          const startCoords = tiptap.view.coordsAtPos(from);

          if (startCoords) {
            // Offset of the result within the editor host
            const hostRect = editorHostNode.getBoundingClientRect?.() || { top: 0 };
            // Account for elements above the editor container (e.g., Draft/Scenes box)
            const editorContainer = window.document.getElementById("editorContainer");
            const containerTop = editorContainer?.offsetTop || 0;
            // Center the result in the viewport
            const viewportHeight = scrollHostRef.current.clientHeight;
            const resultPosition = containerTop + (startCoords.top - hostRect.top);
            scrollHostRef.current.scrollTop = Math.max(0, resultPosition - viewportHeight / 2);
          }

          // Set selection to highlight the result
          tiptap.chain().setTextSelection({ from, to }).run();
        } catch (e) {
          Sentry.captureException(e, { tags: { feature: "find_replace_scroll", field: name } });
        }
      },

      getElement: (): HTMLElement | null => {
        // For DOM ordering, use the editor container (#editorContainer) — it
        // represents where the document editor appears visually.
        const containerElement = window.document.getElementById("editorContainer");
        if (containerElement) return containerElement;

        // Fallback: the editor host node or the ProseMirror root
        return editorHostNode || (tiptap.view?.dom as HTMLElement) || null;
      },
    };
  }, [editor?.tiptap, id, name, scrollHostRef, editorHostNode]);

  useEffect(() => {
    if (searchable) {
      register(searchable);
      return () => unregister(id);
    }
  }, [searchable, register, unregister, id]);

  return searchable;
}

export default useTiptapSearchable;
